// Handles FormExtended widget: multi-column Form Builder layout and HTML body injection

interface ZoneConfig {
    fields: string[];
}

interface SectionProperties {
    ColumnLayout?: string;
    ColumnGutter?: string;
    BackgroundColor?: string;
    Intro?: string;
    TopPadding?: string;
    BottomPadding?: string;
    TopMargin?: string;
    BottomMargin?: string;
}

interface SectionConfig {
    identifier: string;
    properties?: SectionProperties;
    zones?: ZoneConfig[];
}

interface LayoutConfig {
    sections?: SectionConfig[];
}

// ── HTML body injection ────────────────────────────────────────────────────────

function initHtmlBodyInjection(): void {
    document.querySelectorAll<HTMLElement>('.form.form-extended').forEach(container => {
        const template = container.querySelector<HTMLTemplateElement>('.form-extended-html-body');
        if (!template) return;

        function injectHtml(): boolean {
            const submitText = container.querySelector('.formwidget-submit-text');
            if (!submitText) return false;
            if (submitText.querySelector('.content--landing')) return true;
            submitText.innerHTML = '';
            submitText.appendChild(template!.content.cloneNode(true));
            return true;
        }

        // Page-reload submission: element already in DOM
        if (!injectHtml() && window.MutationObserver) {
            // AJAX submission: watch for element to appear
            const observer = new MutationObserver(() => {
                if (injectHtml()) observer.disconnect();
            });
            observer.observe(container, { childList: true, subtree: true });
        }
    });
}

// ── Form Builder multi-column layout ──────────────────────────────────────────

function initFormBuilderLayout(): void {
    const forms = document.querySelectorAll<HTMLElement>('[data-form-builder-layout="true"]');

    forms.forEach(formContainer => {
        const layoutJson = formContainer.getAttribute('data-layout-config');
        if (!layoutJson) return;

        try {
            const layoutConfig: LayoutConfig = JSON.parse(layoutJson);
            applyFormLayout(formContainer, layoutConfig);
        } catch (error) {
            console.error('Failed to parse form layout configuration:', error);
        }
    });
}

function applyFormLayout(formContainer: HTMLElement, layoutConfig: LayoutConfig): void {
    const form = formContainer.querySelector('form');
    if (!form) return;

    const formFields = Array.from(form.querySelectorAll<HTMLElement>('.ktc-form-group, .form-group'));
    if (!layoutConfig.sections || layoutConfig.sections.length === 0) return;

    const newFormContent = document.createElement('div');
    newFormContent.className = 'ktc-form-builder-layout';

    layoutConfig.sections.forEach(section => {
        const sectionElement = createSectionElement(section, formFields);
        if (sectionElement) newFormContent.appendChild(sectionElement);
    });

    const formInner = form.querySelector<HTMLElement>('.ktc-form-fields, .form-fields') ?? form;

    while (
        formInner.firstChild instanceof HTMLElement &&
        (formInner.firstChild.classList.contains('ktc-form-group') ||
            formInner.firstChild.classList.contains('form-group'))
    ) {
        formInner.removeChild(formInner.firstChild);
    }

    formInner.insertBefore(newFormContent, formInner.firstChild);
}

function createSectionElement(section: SectionConfig, formFields: HTMLElement[]): HTMLElement | null {
    if (section.identifier === 'Wakefly.MultiColumnFormSection') {
        return createMultiColumnSection(section, formFields);
    }

    const sectionDiv = document.createElement('div');
    sectionDiv.className = 'form-builder-section';

    section.zones?.forEach(zone => {
        getFieldsForZone(zone, formFields).forEach(field => sectionDiv.appendChild(field));
    });

    return sectionDiv.children.length > 0 ? sectionDiv : null;
}

function createMultiColumnSection(section: SectionConfig, formFields: HTMLElement[]): HTMLElement | null {
    const properties = section.properties ?? {};
    const columnLayout = properties.ColumnLayout ?? '0';

    const sectionDiv = document.createElement('div');
    sectionDiv.className = 'foreground-content';

    const spacingClasses = getSpacingClasses(properties);
    if (spacingClasses) sectionDiv.className += ' ' + spacingClasses;

    if (properties.BackgroundColor) {
        sectionDiv.className += ` text-bg-${properties.BackgroundColor}`;
    }

    if (properties.Intro) {
        const introDiv = document.createElement('div');
        introDiv.innerHTML = properties.Intro;
        sectionDiv.appendChild(introDiv);
    }

    const columnConfig = getColumnConfiguration(columnLayout);

    if (columnConfig.length === 0) {
        const zone = section.zones?.[0];
        if (zone) {
            getFieldsForZone(zone, formFields).forEach(field => sectionDiv.appendChild(field));
        }
    } else {
        const rowDiv = document.createElement('div');
        rowDiv.className = 'row flex-wrap';
        if (properties.ColumnGutter) rowDiv.className += ` gx-${properties.ColumnGutter}`;

        section.zones?.forEach((zone, index) => {
            if (index < columnConfig.length) {
                const colDiv = document.createElement('div');
                colDiv.className = `${columnConfig[index]} d-flex flex-column`;
                getFieldsForZone(zone, formFields).forEach(field => colDiv.appendChild(field));
                rowDiv.appendChild(colDiv);
            }
        });

        sectionDiv.appendChild(rowDiv);
    }

    return sectionDiv.children.length > 0 ? sectionDiv : null;
}

function getSpacingClasses(properties: SectionProperties): string {
    const classes: string[] = [];
    if (properties.TopPadding) classes.push(`pt-${properties.TopPadding}`);
    if (properties.BottomPadding) classes.push(`pb-${properties.BottomPadding}`);
    if (properties.TopMargin) classes.push(`mt-${properties.TopMargin}`);
    if (properties.BottomMargin) classes.push(`mb-${properties.BottomMargin}`);
    return classes.join(' ');
}

function getColumnConfiguration(columnLayout: string): string[] {
    const layoutMap: Record<string, string[]> = {
        '0': [],
        '1': ['col'],
        '2': ['col-12 col-lg-6', 'col-12 col-lg-6'],
        '3': ['col-12 col-lg-4', 'col-12 col-lg-4', 'col-12 col-lg-4'],
        '4': ['col-12 col-lg-3', 'col-12 col-lg-3', 'col-12 col-lg-3', 'col-12 col-lg-3'],
        '2575': ['col-12 col-lg-3', 'col-12 col-lg-9'],
        '3366': ['col-12 col-lg-4', 'col-12 col-lg-8'],
        '4060': ['col-12 col-lg-5', 'col-12 col-lg-7'],
        '6040': ['col-12 col-lg-7', 'col-12 col-lg-5'],
        '6633': ['col-12 col-lg-8', 'col-12 col-lg-4'],
        '7525': ['col-12 col-lg-9', 'col-12 col-lg-3'],
        '252550': ['col-12 col-lg-3', 'col-12 col-lg-3', 'col-12 col-lg-6'],
        '255025': ['col-12 col-lg-3', 'col-12 col-lg-6', 'col-12 col-lg-3'],
        '502525': ['col-12 col-lg-6', 'col-12 col-lg-3', 'col-12 col-lg-3'],
    };
    return layoutMap[columnLayout] ?? [];
}

function getFieldsForZone(zone: ZoneConfig, formFields: HTMLElement[]): HTMLElement[] {
    if (!zone.fields || zone.fields.length === 0) return [];

    return zone.fields.reduce<HTMLElement[]>((acc, fieldName) => {
        const field = formFields.find(f => f.querySelector(`[name="${fieldName}"]`) !== null);
        if (field) acc.push(field);
        return acc;
    }, []);
}

// ── Init ──────────────────────────────────────────────────────────────────────

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initHtmlBodyInjection();
        initFormBuilderLayout();
    });
} else {
    initHtmlBodyInjection();
    initFormBuilderLayout();
}
